/*
 * In-page confirmation for destructive buttons — no window.confirm. The
 * first click ARMS the button: its label swaps to `armedLabel` and it gets
 * a warning tint. A second click while armed runs the action. Focus leaving
 * the button or a few seconds of hesitation disarms it, so a stray click
 * can't fire the action and can't leave the button armed either.
 *
 * The armed label should be a short "Really <verb>?"; a longer explanation
 * of what the action does belongs in the button's `title` (hover), where
 * the reader can see it before ever clicking — window.confirm's paragraph
 * of caveats had no in-page home, this does.
 *
 * The helper owns the button's `onclick`; pass the action instead of
 * assigning a handler. `shouldArm` (default: always) lets a caller skip
 * arming and run the action directly — for validation branches where the
 * action will just show an error, arming first would be noise.
 *
 * The action's promise is awaited and a rejection reported through
 * `pageError` (as "Couldn't <button label>: …"), so an async action's
 * network failure is never a silent unhandled rejection.
 */

import { describe, pageError } from './fetch-helpers.ts';

const ARM_MS = 4000;
const ARMED_TINT = 'var(--error-bg)'; // themed; inline styles resolve var()

export const confirmButton = (
  button: HTMLButtonElement,
  armedLabel: string,
  action: () => unknown,
  shouldArm: () => boolean = () => true,
): void => {
  let disarmTimer: ReturnType<typeof setTimeout> | null = null;
  let restore: (() => void) | null = null;
  // For the failure message: the button's label, or its title when the
  // label is just a glyph ("✕").
  const label = (button.textContent ?? '').trim();
  const doing = (/[a-z]/i.test(label) ? label : button.title || label) || 'complete that action';
  const disarm = () => {
    if (disarmTimer) clearTimeout(disarmTimer);
    disarmTimer = null;
    restore?.();
    restore = null;
  };
  button.addEventListener('blur', disarm);
  button.onclick = () => {
    if (!restore && shouldArm()) {
      const label = button.textContent ?? '';
      const tint = button.style.backgroundColor;
      button.textContent = armedLabel;
      button.style.backgroundColor = ARMED_TINT;
      button.classList.add('armed');
      restore = () => {
        button.textContent = label;
        button.style.backgroundColor = tint;
        button.classList.remove('armed');
      };
      disarmTimer = setTimeout(disarm, ARM_MS);
      return;
    }
    disarm();
    void (async () => {
      try {
        await action();
      } catch (e) {
        console.error(e);
        pageError(describe(e, doing.toLowerCase()));
      }
    })();
  };
};
